/**
 * Admin-side mirror of backend `Configuration.Release` flags.
 * Defaults: safety ON; risky features ON in non-production builds, OFF when `production: true`.
 *
 * Override per environment file or deployment env injection.
 */
export const RELEASE_FEATURE_KEYS = [
  'demoTenantsEnabled',
  'demoProvisioningEnabled',
  'demoLifecycleEnabled',
  'demoLaunchEnabled',
  'demoSeedingEnabled',
  'demoToursEnabled',
  'demoPacksEnabled',
  'externalDemoAccessEnabled',
  'demoAnalyticsEnabled',
  'demoOperationsEnabled',
  'demoConversionEnabled',
  'internalCrmEnabled',
  'demoCrmLinkingEnabled',
  'demoCrmAutomationEnabled',
  'crmCommandCenterEnabled',
  'crmPlaybooksEnabled',
  'crmFollowUpDraftsEnabled',
  'crmDataQualityEnabled',
  'crmGovernanceEnabled',
  'crmAuditViewerEnabled',
  'blockDemoRealPaymentsEnabled',
  'blockDemoPayoutsEnabled',
  'blockDemoPublicDiscoveryEnabled',
  'blockDemoExternalNotificationsEnabled',
  'preventSyntheticCustomersInCrmEnabled',
  'testTenantsEnabled',
  'testProvisioningEnabled',
  'testSeedingEnabled',
] as const;

export type ReleaseFeatureKey = (typeof RELEASE_FEATURE_KEYS)[number];

export type InternalCrmDemoSalesReleaseFlags = Record<ReleaseFeatureKey, boolean>;

function riskyDefault(production: boolean): boolean {
  return !production;
}

function safetyDefault(): boolean {
  return true;
}

/** Build flag map aligned with backend release config defaults. */
export function buildReleaseFlags(production: boolean): InternalCrmDemoSalesReleaseFlags {
  const risky = riskyDefault(production);
  const safety = safetyDefault();
  return {
    demoTenantsEnabled: risky,
    demoProvisioningEnabled: risky,
    demoLifecycleEnabled: risky,
    demoLaunchEnabled: risky,
    demoSeedingEnabled: risky,
    demoToursEnabled: risky,
    demoPacksEnabled: risky,
    externalDemoAccessEnabled: risky,
    demoAnalyticsEnabled: risky,
    demoOperationsEnabled: risky,
    demoConversionEnabled: risky,
    internalCrmEnabled: risky,
    demoCrmLinkingEnabled: risky,
    demoCrmAutomationEnabled: false,
    crmCommandCenterEnabled: risky,
    crmPlaybooksEnabled: risky,
    crmFollowUpDraftsEnabled: risky,
    crmDataQualityEnabled: false,
    crmGovernanceEnabled: safety,
    crmAuditViewerEnabled: risky,
    blockDemoRealPaymentsEnabled: safety,
    blockDemoPayoutsEnabled: safety,
    blockDemoPublicDiscoveryEnabled: safety,
    blockDemoExternalNotificationsEnabled: safety,
    preventSyntheticCustomersInCrmEnabled: safety,
    testTenantsEnabled: risky,
    testProvisioningEnabled: risky,
    testSeedingEnabled: risky,
  };
}

/** Route prefix → required release flag (first match wins). */
export const ROUTE_RELEASE_FLAGS: Array<{ prefix: string; flag: ReleaseFeatureKey }> = [
  { prefix: '/organizations/demo-hub', flag: 'demoTenantsEnabled' },
  { prefix: '/organizations/demo-workspaces/add', flag: 'demoProvisioningEnabled' },
  { prefix: '/organizations/demo-workspaces', flag: 'demoTenantsEnabled' },
  { prefix: '/organizations/demo-pack-catalog', flag: 'demoPacksEnabled' },
  { prefix: '/organizations/demo-analytics', flag: 'demoAnalyticsEnabled' },
  { prefix: '/organizations/demo-operations', flag: 'demoOperationsEnabled' },
  { prefix: '/organizations/demo-sales-pipeline', flag: 'demoTenantsEnabled' },
  { prefix: '/organizations/test-hub', flag: 'testTenantsEnabled' },
  { prefix: '/organizations/test-workspaces/conversions', flag: 'testTenantsEnabled' },
  { prefix: '/organizations/test-workspaces/add', flag: 'testProvisioningEnabled' },
  { prefix: '/organizations/test-workspaces', flag: 'testTenantsEnabled' },
  { prefix: '/crm/sales-command-center', flag: 'crmCommandCenterEnabled' },
  { prefix: '/crm/data-quality', flag: 'crmDataQualityEnabled' },
];

export const MENU_LINK_RELEASE_FLAGS: Record<string, ReleaseFeatureKey> = {
  '/organizations/demo-workspaces/add': 'demoProvisioningEnabled',
  '/organizations/demo-workspaces': 'demoTenantsEnabled',
  '/organizations/demo-pack-catalog': 'demoPacksEnabled',
  '/organizations/demo-analytics': 'demoAnalyticsEnabled',
  '/organizations/demo-operations': 'demoOperationsEnabled',
  '/organizations/demo-sales-pipeline': 'demoTenantsEnabled',
  '/organizations/test-workspaces/conversions': 'testTenantsEnabled',
  '/organizations/test-workspaces/add': 'testProvisioningEnabled',
  '/organizations/test-workspaces': 'testTenantsEnabled',
  '/organizations/test-hub': 'testTenantsEnabled',
  '/crm/sales-command-center': 'crmCommandCenterEnabled',
  '/crm/data-quality': 'crmDataQualityEnabled',
};
